import { Lock } from '@digital-twins/design-system/icons'
import useSWR from 'swr'
import { toast } from 'sonner'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routing/navigation'
import { useTranslation } from 'react-i18next'
import { usersApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { ROUTES } from '@/routes/routes'

export type LockedGateMode = 'free_plan' | 'feature_disabled'

interface LockedFreeGateProps {
  /** Short label for what's gated, e.g. "create studies". */
  action: string
  /** Custom CTA label. Defaults to "Upgrade to unlock". */
  ctaLabel?: string
  /** Where the CTA navigates. Defaults to `ROUTES.billingPlans`. */
  href?: string
  /** Title override for the full-page variant. */
  title?: string
  /** Description override. Defaults to "Upgrade your plan to {action}." */
  description?: string
  /**
   * Why the gate is shown. When omitted, inferred from profile tier:
   * `locked_free` → upgrade copy; any other tier → feature unavailable (no upgrade CTA).
   * Custom `title` / `description` keep the upgrade CTA (e.g. out-of-credits billing).
   */
  reason?: LockedGateMode
  /** Render as an inline blocker (no full-page centering) */
  inline?: boolean
  children?: React.ReactNode
}

export function resolveLockedGateMode(
  subscriptionTier: string | undefined | null,
  options: { reason?: LockedGateMode; hasCustomCopy?: boolean },
): LockedGateMode {
  if (options.reason) {
    return options.reason
  }
  if (options.hasCustomCopy) {
    return 'free_plan'
  }
  if (!subscriptionTier || subscriptionTier === 'locked_free') {
    return 'free_plan'
  }
  return 'feature_disabled'
}

function useGateProfile() {
  const { isAuthenticated } = useAuth()
  const { data: profile } = useSWR(
    isAuthenticated ? 'profile' : null,
    () => usersApi.getProfile(),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
    },
  )
  return profile
}

/** Full-page or inline empty state shown when a user tries to access a gated feature. */
export function LockedFreeGate({
  action,
  ctaLabel,
  href = ROUTES.billingPlans,
  title,
  description,
  reason,
  inline = false,
  children,
}: LockedFreeGateProps) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const { isOwner, isLoading: ownerLoading } = useCurrentOrg()
  const profile = useGateProfile()
  const hasCustomCopy = title !== undefined || description !== undefined
  const mode = resolveLockedGateMode(profile?.subscriptionTier, { reason, hasCustomCopy })
  const isFeatureDisabled = mode === 'feature_disabled'

  const descText = description ?? (
    isFeatureDisabled
      ? t('billing.featureDisabledDescription', { action })
      : t('billing.upgradeToAction', { action })
  )
  const resolvedTitle = title ?? (
    isFeatureDisabled ? t('billing.featureDisabledTitle') : t('billing.freeViewOnly')
  )
  const resolvedCta = ctaLabel ?? t('billing.upgradeToUnlock')
  const showUpgradeCta = !isFeatureDisabled

  function handleUpgradeClick() {
    if (!isOwner) {
      toast.error(t('billing.ownerOnly'))
      return
    }
    router.push(href)
  }

  if (inline) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3">
        <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
        <Text size="sm" color="muted" className="flex-1">
          {descText}
        </Text>
        {showUpgradeCta ? (
          <Button
            size="sm"
            onClick={handleUpgradeClick}
            disabled={ownerLoading}
            data-testid="locked-free-gate-cta"
            className="rounded-[6px] h-[30px] shrink-0"
          >
            {resolvedCta}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center p-12 gap-4 text-center" data-testid="locked-free-gate">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <Lock className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="flex flex-col gap-1 max-w-xs">
        <Text size="base" weight="medium">{resolvedTitle}</Text>
        <Text size="sm" color="muted">{descText}</Text>
      </div>
      {children}
      {showUpgradeCta ? (
        <Button
          onClick={handleUpgradeClick}
          disabled={ownerLoading}
          data-testid="locked-free-gate-cta"
          className="rounded-[6px]"
        >
          {resolvedCta}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Hook to check if the current user is on locked_free tier.
 *
 * Reads from the shared SWR `'profile'` cache (also populated by
 * `UpgradeModalConnected`, with the modal driving a 30s polling refresh).
 * This avoids the "ghost trial" class of bugs where `useAuth().user` is a
 * login-time snapshot that doesn't update when the server transitions an
 * expired trial to `locked_free` mid-session.
 */
export function useIsLockedFree(): boolean {
  const profile = useGateProfile()
  return profile?.subscriptionTier === 'locked_free'
}
